import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/** Where an import goes (04/11/2026): a new campaign by its name, one of the sender's, or none. */
export type CampaignTarget = { sequenceId: string } | { name: string } | null;

interface CampaignChoiceProps {
  toolAppId: string;
  value: CampaignTarget;
  /** The name a new campaign takes until the person types one. */
  suggestedName: string;
  /** From the app's list, the companies may stay in the base only. */
  allowNone: boolean;
  onChange: (value: CampaignTarget) => void;
}

const field = 'w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none';
const NEW = '__new__';
const NONE = '__none__';

export const CampaignChoice = ({ toolAppId, value, suggestedName, allowNone, onChange }: CampaignChoiceProps) => {
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [typed, setTyped] = useState<string | null>(null);

  useEffect(() => {
    // The sender's own campaigns: an import fills only what its sender may prepare.
    void supabase.auth.getUser().then(({ data }) => {
      const userId = data.user?.id;
      if (!userId) return;
      void supabase.from('outreach_sequences').select('id, name').eq('tool_app_id', toolAppId).eq('owner_user_id', userId)
        .order('created_at', { ascending: false })
        .then(({ data: rows }) => setCampaigns(((rows ?? []) as Array<{ id: string; name: string }>)));
    });
  }, [toolAppId]);

  // A new campaign follows the suggestion until the person names it.
  useEffect(() => {
    if (value && 'name' in value && typed === null && value.name !== suggestedName) onChange({ name: suggestedName });
  }, [value, suggestedName, typed, onChange]);

  const selected = value === null ? NONE : 'sequenceId' in value ? value.sequenceId : NEW;
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-domino-inkSoft" htmlFor="import-campaign">Campagne</label>
      <select id="import-campaign" value={selected} className={field}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next === NONE ? null : next === NEW ? { name: typed ?? suggestedName } : { sequenceId: next });
        }}>
        <option value={NEW}>Nouvelle campagne</option>
        {campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
        {allowNone && <option value={NONE}>Aucune, seulement la liste</option>}
      </select>
      {value && 'name' in value && (
        <input aria-label="Nom de la campagne" value={value.name} maxLength={120}
          onChange={(event) => { setTyped(event.target.value); onChange({ name: event.target.value }); }} className={field} />
      )}
    </div>
  );
};
